/**
 * Trovva — Guided Tour Step Definitions
 *
 * Comprehensive walkthroughs for every page. Each step highlights one
 * UI section, dims everything else, and explains what it does in detail.
 *
 * Step shape:
 *   { target, title, icon, content, position: 'top'|'bottom'|'left'|'right' }
 *   target: null  → centered welcome/completion modal
 *   target: 'css' → spotlight that element
 */

import {
  Home, Users, Wrench, Building2, DollarSign, Sparkles, Settings,
  Search, Bell, BarChart3, Phone, FileText, Zap, Award, Upload,
  Shield, Layout, Calendar, Target, Clock, Activity,
  Key, UserCheck, CheckCircle, TrendingUp, ArrowRight, Star,
  MessageSquare, Mic, ListChecks, Trophy, Eye,
} from 'lucide-react';


// ═══════════════════════════════════════════════════════════════
// BROKER DASHBOARD
// ═══════════════════════════════════════════════════════════════
export const DASHBOARD_STEPS = [
  {
    target: null,
    title: 'Welcome to Trovva',
    icon: Sparkles,
    content: 'Trovva is your AI-native brokerage operating system. This tour will walk you through every section of your dashboard so you know exactly where everything is and what it does. Use the arrow keys or click Next to continue.',
  },
  {
    target: '[data-tour="brain-hero"]',
    title: 'Trovva Brain — Your AI Command Center',
    icon: Sparkles,
    position: 'bottom',
    content: 'This is the front door to your AI brain. Type any question — "Any compliance deadlines this week?", "How is Patricia doing?", "Show my pipeline" — and get an instant answer drawn from your real CRM data. You can also click the suggested prompt chips below the input bar for common questions. The brain has access to your leads, deals, tasks, calendar, and NJ compliance rules.',
  },
  {
    target: '[data-tour="pane-toggle"]',
    title: 'Brokerage / Property Management Toggle',
    icon: Home,
    position: 'bottom',
    content: 'Trovva manages both sides of your business. Click "Brokerage" to see sales metrics, agent performance, and deal pipeline. Click "Property Mgmt" to see occupancy, rent collections, maintenance, and lease renewals. The KPI tiles and tables below change based on which pane you select.',
  },
  {
    target: '[data-tour="kpi-tiles"]',
    title: 'Key Performance Indicators',
    icon: BarChart3,
    position: 'bottom',
    content: 'Four KPI tiles give you the state of your business in 5 seconds. In Brokerage mode: Company Dollar MTD (your revenue), Pipeline Value (total deal volume in play), Active Deals, and New Leads This Week with speed-to-lead time. In PM mode: Occupancy rate, Rent Collected, Open Maintenance, and Expiring Renewals. Click any tile to jump directly to that module.',
  },
  {
    target: '[data-tour="agent-snapshot"]',
    title: 'Agent Snapshot',
    icon: Users,
    position: 'bottom',
    content: 'A real-time scorecard for every agent in your brokerage. See their performance grade (A through F based on task completion, response time, and deal activity), pipeline value, and overdue task count. Red overdue numbers mean that agent needs attention. Click "View all" to open the full Agent Activity module with detailed scoring breakdowns.',
  },
  {
    target: '[data-tour="deals-timeline"]',
    title: 'Deals Timeline',
    icon: Target,
    position: 'bottom',
    content: 'Every upcoming milestone across all your active deals, sorted by date. Closings, inspections, attorney review deadlines, mortgage commitments — all in one place. Color-coded dots indicate urgency: red = this week, orange = next 7 days, green = on track. Click "All deals" to open the full Transaction Tracker.',
  },
  {
    target: '[data-tour="attention"]',
    title: 'Needs Your Attention',
    icon: Zap,
    position: 'top',
    content: 'The most important section on your dashboard. This panel surfaces items that need your immediate action — split by Brokerage (counter offers, hot leads, stale follow-ups) and Property Management (urgent maintenance, expiring leases). Each item shows urgency level and time sensitivity. Check this first every morning.',
  },
  {
    target: '[data-tour="schedule"]',
    title: "Today's Schedule",
    icon: Clock,
    position: 'top',
    content: 'Your full schedule for today pulled from your calendar — showings, closings, team huddles, vendor meetings, buyer consultations. Each entry shows the time, the agent involved, and a brief description. This syncs with Google Calendar and Outlook when connected.',
  },
  {
    target: '[data-tour="activity"]',
    title: 'Recent Activity Feed',
    icon: Activity,
    position: 'top',
    content: 'A live feed of what Trovva has been doing for you — auto-responding to new leads, dispatching vendors, flagging lease renewals, sending follow-up reminders. This shows you the AI working in the background so you know nothing is falling through the cracks.',
  },
  {
    target: '[data-tour="sidebar"]',
    title: 'Navigation Sidebar',
    icon: Layout,
    position: 'right',
    content: 'Your main navigation hub. Every module is one click away: Dashboard, CRM, Showings, Agent Activity, Transactions, Listing Hub, Properties & Leases, Maintenance, Financials, Reports, and Settings. Calendar lives inside CRM as a tab, with a quick-access shortcut in the top bar. The sidebar collapses to icons for more screen space — click the arrow at the top to toggle.',
  },
  {
    target: '[data-tour="search"]',
    title: 'Global Search',
    icon: Search,
    position: 'bottom',
    content: 'Search across your entire Trovva workspace — leads, contacts, properties, deals, tasks, and team members. Start typing and results appear instantly with fuzzy matching. Click a result to jump directly to that record.',
  },
  {
    target: '[data-tour="notifications"]',
    title: 'Notifications',
    icon: Bell,
    position: 'bottom',
    content: 'Real-time alerts for everything that matters: new lead assignments, maintenance requests, deal milestone reminders, compliance deadlines, and team activity. The red badge shows your unread count. Click to expand and see details — each notification links to the relevant record.',
  },
  {
    target: null,
    title: "You're Ready to Go!",
    icon: CheckCircle,
    content: "That covers every section of your dashboard. Remember: every page in Trovva has its own \"Guide Me\" button in the bottom-right corner. And the Trovva Brain (the green \"Ask Trovva\" button) is always available to answer questions, look up data, or take actions for you. You can also reach the brain via WhatsApp or phone call.",
  },
];


// ═══════════════════════════════════════════════════════════════
// AGENT DASHBOARD (different from broker)
// ═══════════════════════════════════════════════════════════════
export const AGENT_DASHBOARD_STEPS = [
  {
    target: null,
    title: 'Your Agent Dashboard',
    icon: Home,
    content: 'Welcome! This is your personalized command center. Everything here is scoped to your book of business — your leads, your deals, your commissions, your tasks. Your broker sees the whole team; you see what matters to you.',
  },
  {
    target: '[data-tour="agent-brain-hero"]',
    title: 'Trovva Brain — Ask Anything',
    icon: Sparkles,
    position: 'bottom',
    content: "Type or tap a prompt to ask the AI brain anything. \"What's on my plate today?\" gives you a quick morning briefing. \"Who should I follow up with?\" checks your stale leads. \"Show my pipeline\" summarizes your active deals. The brain knows your CRM data, calendar, and tasks in real time.",
  },
  {
    target: '[data-tour="agent-stats"]',
    title: 'Your Numbers',
    icon: BarChart3,
    position: 'bottom',
    content: 'Four tiles with your personal KPIs: My Leads (total assigned to you, with new count), Active Deals (with pipeline dollar value), YTD Commission (earned so far with pending amount), and Closings Soon (deals closing in the next 14 days). Click any tile to jump to the full module.',
  },
  {
    target: '[data-tour="whatsapp-tip"]',
    title: 'WhatsApp Integration',
    icon: MessageSquare,
    position: 'bottom',
    content: 'You can manage tasks without opening the app. Reply to your morning WhatsApp briefing with "Done 1 3" to close tasks #1 and #3, or send a voice note like "finished the 2pm showing" and the AI will match it to the right task. Your broker sets this up during onboarding.',
  },
  {
    target: '[data-tour="leaderboard"]',
    title: 'Team Leaderboard',
    icon: Trophy,
    position: 'bottom',
    content: "See how you rank among your teammates. Scoring is based on task completion rate, speed-to-lead response, and deal activity — all positive metrics, no shaming. Your position is highlighted with a \"YOU\" tag. Healthy competition drives results. Click \"View full leaderboard\" for detailed breakdowns.",
  },
  {
    target: '[data-tour="agent-tasks"]',
    title: "Today's Tasks",
    icon: ListChecks,
    position: 'bottom',
    content: "Your task list for today — calls to make, showings to prep, follow-ups to send. Click the circle to mark a task complete (it syncs instantly). Overdue tasks from previous days appear here too with a red warning. If you have more than 5 tasks, click \"Show all in My Day\" for the full task management view.",
  },
  {
    target: '[data-tour="agent-actions"]',
    title: 'Quick Actions',
    icon: Sparkles,
    position: 'bottom',
    content: 'One-click shortcuts to common workflows: Add a Lead (opens the lead form), AI Tools (listing generator, social post creator), My Earnings (commission tracker), and Open My Day (full daily planner with drag-and-drop task management).',
  },
  {
    target: null,
    title: "That's Your Dashboard!",
    icon: CheckCircle,
    content: 'You now know every section. Pro tip: the \"Ask Trovva\" button in the bottom-right is always available on every page. Ask it anything — check your pipeline, find a contact, log a showing, or get NJ compliance guidance. You can also talk to it via WhatsApp or phone.',
  },
];


// ═══════════════════════════════════════════════════════════════
// CRM — PER-TAB TOURS
// Each CRM sub-tab gets its own focused tour. The GuidedTour
// component inside LeadsView picks the right one based on crmTab.
// ═══════════════════════════════════════════════════════════════

// ── Inbox tour (default CRM landing tab) ──
export const CRM_INBOX_STEPS = [
  {
    target: null,
    title: 'Welcome to Your CRM',
    icon: Users,
    content: 'This is your complete lead management system. Leads flow in automatically from Zillow, Facebook, Gmail, and your website — or you add them manually. Track every lead from first contact through close. Let\'s walk through the key controls first.',
  },
  {
    target: '[data-tour="add-lead"]',
    title: 'Add Leads & Import',
    icon: Users,
    position: 'bottom',
    content: 'Click "+ Add Lead" to manually enter a new contact with name, phone, email, source, and property interest. "Import CSV" lets you bulk-import contacts from a spreadsheet — the AI auto-maps your columns. "Export CSV" downloads your current leads for use in other tools. All imports are de-duplicated automatically.',
  },
  {
    target: '[data-tour="lead-nav"]',
    title: 'CRM Tabs',
    icon: Layout,
    position: 'bottom',
    content: 'Your CRM is organized into tabs: Inbox (triage new leads), My Board (Kanban pipeline), Tasks (to-dos), Calendar (schedule), Segments (saved smart lists), Action Plans (drip sequences), Routing (lead assignment rules), and Automations (trigger-based workflows). Each tab has its own Guide Me tour — click "Guide Me" when you\'re on any tab to learn it.',
  },
  {
    target: '[data-tour="inbox-toggle"]',
    title: 'Triage vs Feed',
    icon: Target,
    position: 'bottom',
    content: 'Inbox has two modes. Triage shows inbound leads that need your immediate attention — sorted by urgency with action buttons. Feed shows a chronological stream of all CRM activity across your team. Triage is your speed-to-lead queue — new leads from Zillow and Facebook land here first.',
  },
  {
    target: '[data-tour="inbox-actions"]',
    title: 'Quick Actions',
    icon: Phone,
    position: 'bottom',
    content: 'Each lead in Triage has one-tap actions: Call, Text, Email, Claim (assign to yourself), Snooze (revisit later), Pass (send to pond for another agent), and Assign (route to a specific agent). Speed-to-lead matters — the faster you respond, the higher your conversion rate.',
  },
  {
    target: '[data-tour="inbox-row"]',
    title: 'Click to Open Full View',
    icon: ArrowRight,
    position: 'bottom',
    content: 'Click on any lead\'s name to open their full 3-pane detail view. Left pane shows contact info and pipeline status. Center pane shows the communication timeline — calls, texts, emails, notes. Right pane has tasks, deals, action plans, and web activity. You can take all the same actions from there.',
  },
  {
    target: null,
    title: 'Inbox Tour Complete!',
    icon: CheckCircle,
    content: 'Your Inbox is your speed-to-lead command center. New leads land in Triage automatically. Reminderly sends you a WhatsApp nudge if leads sit untouched for 48 hours. Click "My Board" to see your full pipeline, and hit "Guide Me" there for that tour.',
  },
];

// ── My Board tour ──
export const CRM_BOARD_STEPS = [
  {
    target: null,
    title: 'My Board — Your Pipeline',
    icon: Target,
    content: 'My Board is your visual pipeline. Every lead is a card that moves through stages from New to Offer Accepted. Drag and drop to advance leads, or click to open the full detail view. Brokers can filter by agent to see any team member\'s pipeline.',
  },
  {
    target: '[data-tour="board-view-toggle"]',
    title: 'List vs Kanban View',
    icon: Layout,
    position: 'bottom',
    content: 'Toggle between two views. Kanban shows leads as cards in columns organized by stage — drag cards between columns to update status. List view shows a sortable table with all lead details in rows. Your preference is saved automatically.',
  },
  {
    target: '[data-tour="lead-filters"]',
    title: 'Search & Filters',
    icon: Search,
    position: 'bottom',
    content: 'Find any lead instantly. Search by name, email, phone, or source. Filter by type (buyer, seller, renter, investor), status (pipeline stage), tag, and agent. The agent filter lets brokers switch between "My Leads", "All Leads", or a specific agent\'s pipeline.',
  },
  {
    target: '[data-tour="kanban-board"]',
    title: 'Pipeline Stages',
    icon: ArrowRight,
    position: 'bottom',
    content: 'Six stages: New (just arrived), Contacted (first outreach made), Qualified (budget, timeline confirmed), Active Search (actively viewing properties), Offer Submitted, and Offer Accepted (converts to a Deal in Transactions). Each card shows the lead score, staleness badge, contact attempt counter, and tags. Drag between columns or click to open.',
  },
  {
    target: null,
    title: 'My Board Tour Done!',
    icon: CheckCircle,
    content: 'Pro tip: leads stuck in "New" too long get flagged automatically. Reminderly sends your agents a WhatsApp nudge listing stale leads — they can reply with a number to mark a contact attempt without opening the CRM. After 5 failed attempts, the lead auto-moves to cold.',
  },
];

// ── Tasks tour ──
export const CRM_TASKS_STEPS = [
  {
    target: null,
    title: 'Tasks',
    icon: ListChecks,
    content: 'Your to-do list for lead follow-ups, showings, calls, and anything else. Tasks can be created manually, generated by action plans, or assigned by your broker. Each task is tied to a lead and an agent.',
  },
  {
    target: '[data-tour="task-ranges"]',
    title: 'Date Ranges',
    icon: Clock,
    position: 'bottom',
    content: 'Filter tasks by time: Today (what\'s due now), This Week, Overdue (past due and still pending — tackle these first), Upcoming (next 14 days), and All. The number beside "Overdue" tells you how many tasks need immediate attention.',
  },
  {
    target: '[data-tour="task-agent-filter"]',
    title: 'Agent Filter',
    icon: Users,
    position: 'bottom',
    content: 'Brokers can filter by agent: "My Tasks" shows your own, "All Agents" shows the whole team, or pick a specific agent to see their workload. Agents only see their own tasks.',
  },
  {
    target: '[data-tour="task-add"]',
    title: 'Create & Assign Tasks',
    icon: Star,
    position: 'bottom',
    content: 'Click "+ Add Task" to create a new task. Set the title, due date, priority (low/medium/high/urgent), and assign to any agent on your team. The assigned agent receives an instant notification via SMS, WhatsApp, and email. You can also create tasks from within a lead\'s detail view.',
  },
  {
    target: null,
    title: 'Tasks Tour Done!',
    icon: CheckCircle,
    content: 'Tasks sync with your WhatsApp briefing. Your morning digest lists today\'s tasks, and you can reply "Done 1 3" to mark tasks #1 and #3 complete right from your phone.',
  },
];

// ── Calendar tour ──
export const CRM_CALENDAR_STEPS = [
  {
    target: null,
    title: 'Calendar',
    icon: Calendar,
    content: 'Your schedule synced with Google Calendar and Microsoft Outlook. Showings, closings, inspections, team meetings, and personal events — all in one view. Events from deal milestones and task due dates appear automatically.',
  },
  {
    target: '[data-tour="calendar-view"]',
    title: 'Calendar View',
    icon: Calendar,
    position: 'bottom',
    content: 'Switch between Agenda and Month views. Agenda groups events into Today, Tomorrow, Later This Week, and beyond. Month view shows a grid with event dots. Click any event for details including time, attendees, and location. You can also access the calendar quickly from the calendar icon in the top bar.',
  },
  {
    target: null,
    title: 'Calendar Done!',
    icon: CheckCircle,
    content: 'Your morning WhatsApp briefing includes today\'s schedule. Ask the brain "What\'s on my calendar today?" from any channel.',
  },
];

// ── Segments tour ──
export const CRM_SEGMENTS_STEPS = [
  {
    target: null,
    title: 'Segments — Smart Lists',
    icon: Target,
    content: 'Segments are dynamic contact lists that update automatically based on rules you define. Create a segment for "Hot buyers with score > 75" or "Sellers in Cherry Hill" and it always shows the latest matching contacts.',
  },
  {
    target: '[data-tour="segment-tabs"]',
    title: 'Your Segments',
    icon: Layout,
    position: 'bottom',
    content: 'Each segment appears as a tab. Click to view the contacts that match its rules. You can edit the rules, rename, or delete any segment. Create new segments with the "+ New Segment" button — define filters for lead type, status, score range, location, tags, and staleness.',
  },
  {
    target: null,
    title: 'Segments Done!',
    icon: CheckCircle,
    content: 'Segments power your Action Plans and Automations. You can set up a drip sequence that targets a specific segment, or trigger an automation when a contact enters or leaves a segment.',
  },
];

// ── Action Plans tour ──
export const CRM_ACTION_PLANS_STEPS = [
  {
    target: null,
    title: 'Action Plans — Drip Sequences',
    icon: Zap,
    content: 'Action Plans are automated follow-up sequences. Define a series of touches — emails, texts, tasks, wait periods — and assign them to leads. The system executes each step automatically on schedule, so no lead goes unfollowed.',
  },
  {
    target: '[data-tour="plan-list"]',
    title: 'Your Plans',
    icon: ListChecks,
    position: 'bottom',
    content: 'Each plan shows its name, number of steps, and how many contacts are currently enrolled. Click to edit the steps — add emails, SMS, tasks, and wait periods. You can pause or archive plans that are no longer needed.',
  },
  {
    target: null,
    title: 'Action Plans Done!',
    icon: CheckCircle,
    content: 'Assign a plan to any lead from their detail view under the "Plans" tab. You can also auto-enroll leads from a specific source — e.g., all Zillow leads get your "New Buyer Nurture" plan automatically.',
  },
];

// ── Routing tour ──
export const CRM_ROUTING_STEPS = [
  {
    target: null,
    title: 'Lead Routing',
    icon: ArrowRight,
    content: 'Lead Routing controls how new inbound leads get assigned to agents. Set up rules based on lead source, type, location, or round-robin distribution. When a lead arrives from Zillow or Facebook, routing assigns it to the right agent instantly.',
  },
  {
    target: '[data-tour="routing-rules"]',
    title: 'Routing Rules',
    icon: Target,
    position: 'bottom',
    content: 'Define rules with conditions (source = Zillow, type = buyer, city = Cherry Hill) and an assignment method: specific agent, round-robin across a group, or based on capacity. Rules are evaluated in order — first match wins.',
  },
  {
    target: null,
    title: 'Routing Done!',
    icon: CheckCircle,
    content: 'Once routing is configured, new leads from Zillow, Facebook, and email parsing are assigned automatically. The assigned agent gets an instant SMS + WhatsApp notification with the lead details.',
  },
];

// ── Automations tour ──
export const CRM_AUTOMATIONS_STEPS = [
  {
    target: null,
    title: 'Automations',
    icon: Zap,
    content: 'Automations run workflows in the background based on triggers. When something happens (new lead, status change, task completed, deal milestone), the automation executes actions (send email, create task, update status, notify agent). Set it and forget it.',
  },
  {
    target: '[data-tour="automation-list"]',
    title: 'Your Automations',
    icon: ListChecks,
    position: 'bottom',
    content: 'Each automation shows its trigger, conditions, and actions. Toggle the switch to enable/disable. Click to edit — choose from 12 triggers (lead created, status changed, score threshold, etc.) and 12 actions (send email, create task, assign agent, etc.). Conditions let you narrow when the automation fires.',
  },
  {
    target: null,
    title: 'Automations Done!',
    icon: CheckCircle,
    content: 'Start simple: create an automation that sends a welcome email when a new lead is created, or one that creates a follow-up task when a lead moves to "Contacted". The automation log shows every execution so you can verify it\'s working.',
  },
];

// Map CRM sub-tab IDs to their tour steps
export const CRM_TAB_TOURS = {
  inbox: CRM_INBOX_STEPS,
  people: CRM_BOARD_STEPS,
  tasks: CRM_TASKS_STEPS,
  calendar: CRM_CALENDAR_STEPS,
  smart_lists: CRM_SEGMENTS_STEPS,
  action_plans: CRM_ACTION_PLANS_STEPS,
  routing: CRM_ROUTING_STEPS,
  automations: CRM_AUTOMATIONS_STEPS,
};

// Keep backward compat — LEADS_STEPS defaults to Inbox tour
export const LEADS_STEPS = CRM_INBOX_STEPS;


// ═══════════════════════════════════════════════════════════════
// MAINTENANCE
// ═══════════════════════════════════════════════════════════════
export const MAINTENANCE_STEPS = [
  {
    target: null,
    title: 'Maintenance Requests',
    icon: Wrench,
    content: 'Track every maintenance request from tenant submission to vendor completion. The AI automatically triages urgency, categorizes issues, and suggests vendor assignments. Tenants can submit requests via WhatsApp or the tenant portal.',
  },
  {
    target: '[data-tour="maint-stats"]',
    title: 'Request Overview',
    icon: BarChart3,
    position: 'bottom',
    content: "Four counters at the top: Total Requests (all open and closed), Emergency/Urgent (critical safety or habitability issues — these go red when active), New/Untriaged (submitted but not yet categorized by AI), and In Progress (vendor dispatched, work underway). The Emergency count needs same-day attention per NJ tenant law.",
  },
  {
    target: '[data-tour="maint-card"]',
    title: 'Request Cards',
    icon: Wrench,
    position: 'bottom',
    content: "Each card shows the property address, unit, tenant name, issue description, priority level (color-coded), category, date submitted, and current status. Click any card to expand and see the full AI triage results: priority, category, estimated cost, recommended response time, and suggested vendor. You can update status, add notes, and assign vendors directly.",
  },
  {
    target: '[data-tour="maint-ai"]',
    title: 'AI Triage All',
    icon: Zap,
    position: 'bottom',
    content: 'Click "AI Triage All" to automatically process every untriaged request at once. The AI reads each description and determines: urgency level (using NJ habitability standards), issue category (plumbing, electrical, HVAC, appliance, structural), estimated repair cost, recommended response time, and a suggested vendor from your approved list. You can also create new requests with "New Request" — the AI triages each one on submit.',
  },
  {
    target: null,
    title: 'Maintenance Handled!',
    icon: CheckCircle,
    content: 'Pro tip: your morning WhatsApp briefing includes any overnight maintenance requests marked as urgent. The brain can also dispatch vendors via voice — just call and say "send ComfortAir to 85 Maple Ave for the HVAC issue" and it will create the assignment.',
  },
];


// ═══════════════════════════════════════════════════════════════
// TRANSACTIONS
// ═══════════════════════════════════════════════════════════════
export const TRANSACTIONS_STEPS = [
  {
    target: null,
    title: 'Transaction Tracker',
    icon: Building2,
    content: 'Your deal pipeline from accepted offer to closing day. Every NJ-specific milestone is tracked with deadline alerts. No more spreadsheets — all your active deals in one view with compliance checks built in.',
  },
  {
    target: '[data-tour="deal-stats"]',
    title: 'Pipeline Summary',
    icon: BarChart3,
    position: 'bottom',
    content: 'Four tiles at the top: Active Deals (total in your pipeline), Closing This Month (deals with imminent closing dates plus agent payout totals), Commission Forecast (projected revenue across all active deals), and At Risk (deals with overdue milestones or red flags that need your attention).',
  },
  {
    target: '[data-tour="deal-deadlines"]',
    title: 'Deal Triage Alerts',
    icon: Zap,
    position: 'bottom',
    content: "When deals have issues, they surface here as a triage feed with severity badges (HIGH or MED). Each alert shows the property address and what needs attention — overdue attorney review, missing mortgage commitment, stale inspections. Click any alert to jump directly to that deal's detail drawer.",
  },
  {
    target: '[data-tour="deal-pipeline"]',
    title: 'Deal Board & Search',
    icon: Target,
    position: 'bottom',
    content: 'Search and filter your deals by address, client, or agent. Toggle between Kanban view (deals in swim lanes by stage: Attorney Review → Diligence → Financing → Clear to Close) or List view (a table with all deal details). Pre-contract stages live in the CRM pipeline — deals enter Transactions when the offer is accepted. Click any deal card for the full detail drawer with NJ-specific milestones.',
  },
  {
    target: null,
    title: 'Deals Covered!',
    icon: CheckCircle,
    content: 'The brain checks your deal deadlines daily using deterministic date math — no AI guessing. Attorney Review windows, mortgage commitment dates, and closing deadlines are all calculated precisely per NJ contract law timelines.',
  },
];


// ═══════════════════════════════════════════════════════════════
// FINANCIALS
// ═══════════════════════════════════════════════════════════════
export const FINANCIALS_STEPS = [
  {
    target: null,
    title: 'Financial Overview',
    icon: DollarSign,
    content: 'Your brokerage and property management finances in one place. Revenue tracking, commission splits, rent collections, expense reconciliation, and cash flow visibility. Numbers update from your actual deal and rent data.',
  },
  {
    target: '[data-tour="fin-brokerage"]',
    title: 'Brokerage & Property Management',
    icon: DollarSign,
    position: 'bottom',
    content: 'Two clickable entity cards: Brokerage Company shows Revenue MTD, Pending Commissions, Active Deals, and number of agents. Property Management shows Revenue MTD, Pending Amount, Active Units, and Properties. Click either card to drill down into detailed agent-by-agent or property-by-property financials.',
  },
  {
    target: '[data-tour="fin-reconciliation"]',
    title: 'Reconciliation & Recent Transactions',
    icon: CheckCircle,
    position: 'bottom',
    content: 'The Reconciliation table shows the status of each trust/escrow account — whether it is reconciled or needs attention, and the last reconciliation date. NJ requires strict separation of client funds. Below that, Recent Transactions lists the latest financial activity with date, entity (Brokerage vs PM), and amount.',
  },
  {
    target: null,
    title: 'Finances Clear!',
    icon: CheckCircle,
    content: 'Your morning briefing includes a financial snapshot with any overdue rents or trust account alerts. The brain can answer questions like "How much rent is outstanding?" or "What\'s my company dollar this month?" from any channel.',
  },
];


// ═══════════════════════════════════════════════════════════════
// AI TOOLS (Studio)
// ═══════════════════════════════════════════════════════════════
export const AI_TOOLS_STEPS = [
  {
    target: null,
    title: 'AI-Powered Studio',
    icon: Sparkles,
    content: "Your AI content creation suite. Generate MLS listings, social media posts, email templates, and marketing materials — all tailored to South Jersey market language and your brokerage's style. Every output is AI-generated and should be reviewed before publishing.",
  },
  {
    target: '[data-tour="ai-listing"]',
    title: 'Tool Tabs: Listing, Social & Email',
    icon: FileText,
    position: 'bottom',
    content: "Three tools in one: Listing Generator (creates MLS descriptions for sale or rental — choose Professional, Warm & Inviting, Luxury, or Investor-Focused style), Social Media (generates Instagram and Facebook posts with hashtags), and Email Templates (buyer/seller outreach emails). Toggle between Sale and Rent mode in each tool. Enter property details and click Generate — copy the output with one click.",
  },
  {
    target: null,
    title: 'Studio Tour Complete!',
    icon: CheckCircle,
    content: "All AI-generated content includes a disclaimer. Always review outputs before posting — the AI is a starting point, not a final draft. You can also ask the Trovva Brain to generate content from any channel: \"Write an Instagram post for 456 Cherry Hill Rd\" works in the web chat, WhatsApp, or voice.",
  },
];


// ═══════════════════════════════════════════════════════════════
// TEAM & HR (Settings)
// ═══════════════════════════════════════════════════════════════
export const TEAM_STEPS = [
  {
    target: null,
    title: 'Team Management & HR',
    icon: Shield,
    content: "Manage your entire team — 1099 agents and W-2 employees — with compliance tracking, document management, and onboarding workflows. NJ requires specific licenses, E&O insurance, and continuing education. Trovva tracks all of it.",
  },
  {
    target: '[data-tour="team-stats"]',
    title: 'Team Overview Metrics',
    icon: Users,
    position: 'bottom',
    content: "Four counts: Total Team (everyone in your org), 1099 Agents (independent contractors), W-2 Staff (employees), and Needs Attention (anyone with missing documents, expiring licenses, or pending onboarding steps). The \"Needs Attention\" count is your compliance priority — address these to stay in good standing with NJ REC.",
  },
  {
    target: '[data-tour="team-alerts"]',
    title: 'Compliance Alerts',
    icon: Zap,
    position: 'bottom',
    content: "Critical compliance items: unconfirmed team members (haven't accepted their invite), NJ real estate licenses expiring within 60 days, E&O insurance policies about to lapse, and missing required documents. Each alert links to the team member's profile. NJ license renewals happen every 2 years — Trovva starts alerting 90 days before expiry.",
  },
  {
    target: '[data-tour="team-add"]',
    title: 'Add Team Members',
    icon: UserCheck,
    position: 'bottom',
    content: 'Click "Add Team Member" to start onboarding. Choose between 1099 Agent or W-2 Employee — the required documents change for each type. Agents need: NJ real estate license, E&O certificate, W-9, photo ID, and Independent Contractor Agreement. Employees need: I-9, W-4, and direct deposit info. The system sends an email invite to set up their account.',
  },
  {
    target: '[data-tour="team-docs"]',
    title: 'Document Upload with AI Auto-Fill',
    icon: Upload,
    position: 'bottom',
    content: "Upload any required document (license, E&O cert, W-9) and the AI reads it automatically. It extracts the license number, expiration date, carrier name, policy number, and fills in the form fields for you. No more manual data entry from scanned documents. Supported formats: PDF, JPG, PNG.",
  },
  {
    target: '[data-tour="team-member"]',
    title: 'Team Member Profiles',
    icon: Users,
    position: 'right',
    content: "Click any team member to expand their full profile: contact info, role, NJ license details, E&O status, onboarding progress (checklist with checkboxes), uploaded documents, and compliance status. You can edit any field inline. The green/yellow/red indicator shows their overall compliance health. Green = all documents current. Yellow = something expiring soon. Red = missing or expired.",
  },
  {
    target: null,
    title: 'Team Management Complete!',
    icon: CheckCircle,
    content: "Your morning briefing includes team compliance alerts — any licenses or E&O policies expiring this month. The brain monitors these dates automatically using calendar math, not AI inference, so the dates are always accurate.",
  },
];


// ═══════════════════════════════════════════════════════════════
// COMMISSIONS (My Earnings)
// ═══════════════════════════════════════════════════════════════
export const COMMISSIONS_STEPS = [
  {
    target: null,
    title: 'My Earnings',
    icon: DollarSign,
    content: "Track your commissions as a 1099 independent contractor. See what you've earned, what's pending, what's been paid, and plan for quarterly tax payments. All calculations are based on your actual closed deals and your split agreement with the brokerage.",
  },
  {
    target: '[data-tour="earnings-summary"]',
    title: 'Year-to-Date Summary',
    icon: DollarSign,
    position: 'bottom',
    content: "Four key numbers: YTD Earnings (your gross commissions this year), Paid (what you've received from closed deals), Pending (earned but not yet paid — deals still in process), and Avg per Deal. Below the tiles, a tax estimate banner shows your ~30% estimated liability for quarterly payment planning. As a 1099 contractor, you're responsible for self-employment tax.",
  },
  {
    target: '[data-tour="earnings-chart"]',
    title: 'Monthly Earnings & Commission Split',
    icon: TrendingUp,
    position: 'bottom',
    content: "Two panels side by side: the Monthly Earnings bar chart shows your commissions month by month (the current month is highlighted) — helps you spot seasonal patterns. The Commission Breakdown shows your Total Gross, Your Split, Brokerage Split, and Total Volume across all deals.",
  },
  {
    target: '[data-tour="earnings-deals"]',
    title: 'Deal History',
    icon: FileText,
    position: 'top',
    content: "Every deal listed with property address, client name, type (buyer/seller), sale price, your payout, closing date, and payment status (paid/pending). Filter by All, Paid, or Pending using the buttons at the top. Click any deal to expand and see the full commission waterfall — gross amount, brokerage split, your split, and any deductions.",
  },
  {
    target: null,
    title: 'Earnings Tour Done!',
    icon: CheckCircle,
    content: 'Ask the brain "What are my pending commissions?" or "How much have I earned this year?" from any channel and it will pull these numbers in real time.',
  },
];


// ═══════════════════════════════════════════════════════════════
// CALENDAR
// ═══════════════════════════════════════════════════════════════
export const CALENDAR_STEPS = [
  {
    target: null,
    title: 'Calendar',
    icon: Calendar,
    content: "Your schedule synced with Google Calendar and Microsoft Outlook. See showings, closings, inspections, team meetings, and personal events in one view. Events created in Trovva (like deal milestones or task due dates) appear here automatically.",
  },
  {
    target: '[data-tour="calendar-view"]',
    title: 'Calendar View',
    icon: Calendar,
    position: 'bottom',
    content: "Switch between Agenda and Month views using the toggle at the top right. Agenda view groups events into Today, Tomorrow, Later This Week, Next Week, and Later — making it easy to scan what's coming up. Month view shows a full calendar grid with event dots on each day. Click any event for details including time, attendees, and location.",
  },
  {
    target: null,
    title: 'Calendar Covered!',
    icon: CheckCircle,
    content: "Your morning WhatsApp briefing includes today's schedule. You can also ask the brain \"What's on my calendar today?\" or \"When is the 456 Cherry Hill closing?\" from any channel.",
  },
];


// ═══════════════════════════════════════════════════════════════
// LISTING HUB
// ═══════════════════════════════════════════════════════════════
export const LISTINGS_STEPS = [
  {
    target: null,
    title: 'Listing Hub',
    icon: Eye,
    content: "Manage all your property listings in one place. Create new listings, track active/pending/sold status, generate marketing materials, and monitor showing activity. Each listing connects to its deal in the Transaction Tracker.",
  },
  {
    target: '[data-tour="listing-cards"]',
    title: 'Your Listings',
    icon: Home,
    position: 'bottom',
    content: "Each listing row shows the property address, city, type (residential/commercial), price, status, and a colored dot. Click any listing to see the full detail view with all field mappings across platforms. Click \"+ New Listing\" to create a new one — enter property details once and copy them to Bright MLS, LoopNet, Crexi, and your Wix website. Includes photo management with drag-to-reorder and cover shot selection.",
  },
  {
    target: null,
    title: 'Listing Hub Done!',
    icon: CheckCircle,
    content: 'Use the AI Studio to generate MLS descriptions and social posts for any listing. The brain can also help — ask "Write a listing description for 123 Main St" and it will create one based on the property details in your system.',
  },
];


// ═══════════════════════════════════════════════════════════════
// ACTIVITY (Agent Activity / Team Performance)
// ═══════════════════════════════════════════════════════════════
export const ACTIVITY_STEPS = [
  {
    target: null,
    title: 'Agent Activity & Performance',
    icon: Activity,
    content: "Your team's performance dashboard. Every agent scored on task completion, lead response time, deal activity, and compliance. Use this to coach underperformers, recognize top producers, and spot trends before they become problems.",
  },
  {
    target: '[data-tour="activity-roster"]',
    title: 'Agent Table',
    icon: Users,
    position: 'bottom',
    content: "One row per agent with 11 columns: Score (letter grade A–F with numeric score), Overdue tasks, Due Today, On Track, Active Leads, Speed to Lead (response time in minutes), Follow-ups (7-day count), Pipeline $ (total deal value), Last Activity, Trend sparkline, and Actions (Smart Ping). Click any agent row to expand their detailed scorecard with task breakdown and recent activity timeline.",
  },
  {
    target: '[data-tour="activity-segments"]',
    title: 'Table / Leaderboard Toggle',
    icon: BarChart3,
    position: 'bottom',
    content: 'Switch between Table view (detailed metrics for management) and Leaderboard view (ranked by performance score with positive metrics only — completions, speed-to-lead, pipeline value). The leaderboard highlights your position with a "YOU" tag. Use the search box to find a specific agent quickly.',
  },
  {
    target: null,
    title: 'Activity Tour Done!',
    icon: CheckCircle,
    content: 'Your morning briefing includes a team workload summary with any agents who have concerning metrics. Ask the brain "How is Patricia doing?" for a specific agent\'s performance snapshot.',
  },
];


// ═══════════════════════════════════════════════════════════════
// TOUR MAP — connects tab IDs to step arrays
// ═══════════════════════════════════════════════════════════════
export const TOUR_MAP = {
  dashboard: DASHBOARD_STEPS,
  // leads: handled per-tab inside LeadsView.jsx via CRM_TAB_TOURS
  maintenance: MAINTENANCE_STEPS,
  transactions: TRANSACTIONS_STEPS,
  financials: FINANCIALS_STEPS,
  ai_tools: AI_TOOLS_STEPS,
  settings: TEAM_STEPS,
  agent_dashboard: AGENT_DASHBOARD_STEPS,
  commissions: COMMISSIONS_STEPS,
  calendar: CALENDAR_STEPS,
  listings: LISTINGS_STEPS,
  activity: ACTIVITY_STEPS,
};
